<script lang="ts">
	import * as Dialog from '$lib/components/ui/dialog/index.js';
	import { pb } from '@/utils/ApiClient';

	interface Props {
		open: boolean;
		photosList: string[];
		record: { id: string; [key: string]: any };
		initialIndex?: number;
		alt?: string;
	}

	let { open = $bindable(), photosList, record, initialIndex = 0, alt = 'Foto' }: Props = $props();

	let currentDialogImageIndex = $state(0);
	let touchStartX = $state(0);

	const SWIPE_THRESHOLD_PX = 50;

	let fullSizeImageUrl = $derived.by(() => {
		if (photosList.length === 0 || !record) return '';
		return pb.files.getURL(record, photosList[currentDialogImageIndex], { thumb: '0x900' });
	});

	$effect(() => {
		if (open && initialIndex >= 0 && initialIndex < photosList.length) {
			currentDialogImageIndex = initialIndex;
		}
	});

	function nextDialogImage() {
		if (currentDialogImageIndex < photosList.length - 1) {
			currentDialogImageIndex++;
		}
	}

	function previousDialogImage() {
		if (currentDialogImageIndex > 0) {
			currentDialogImageIndex--;
		}
	}

	function handleTouchStart(event: TouchEvent) {
		touchStartX = event.touches[0]?.clientX ?? 0;
	}

	function handleTouchEnd(event: TouchEvent) {
		const touchEndX = event.changedTouches[0]?.clientX ?? 0;
		const deltaX = touchStartX - touchEndX;

		if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;

		if (deltaX > 0) {
			nextDialogImage();
		} else {
			previousDialogImage();
		}
	}

	function handleDialogKeydown(event: KeyboardEvent) {
		if (!open) return;

		if (event.key === 'ArrowRight') {
			event.preventDefault();
			nextDialogImage();
		} else if (event.key === 'ArrowLeft') {
			event.preventDefault();
			previousDialogImage();
		}
	}

	$effect(() => {
		if (open) {
			document.addEventListener('keydown', handleDialogKeydown);
			return () => {
				document.removeEventListener('keydown', handleDialogKeydown);
			};
		}
	});
</script>

<Dialog.Root bind:open={open}>
	<Dialog.Content
		class="max-h-[90vh] w-[95vw] overflow-hidden p-2 sm:max-w-[95vw] md:w-[95vw] lg:w-[98vw] lg:max-w-[98vw]"
	>
		{#if fullSizeImageUrl && photosList.length > 0}
			<div class="relative w-full">
				<div
					role="group"
					aria-label="Imagen ampliada"
					class="flex touch-pan-y items-center justify-center"
					ontouchstart={handleTouchStart}
					ontouchend={handleTouchEnd}
				>
					<img src={fullSizeImageUrl} alt={alt} class="max-h-[85vh] max-w-full object-contain" />
				</div>

				{#if photosList.length > 1}
					<button
						type="button"
						class="absolute top-1/2 left-4 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70 disabled:opacity-50"
						onclick={previousDialogImage}
						disabled={currentDialogImageIndex === 0}
						aria-label="Imagen anterior"
					>
						<svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M15 19l-7-7 7-7"
							></path>
						</svg>
					</button>

					<button
						type="button"
						class="absolute top-1/2 right-4 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70 disabled:opacity-50"
						onclick={nextDialogImage}
						disabled={currentDialogImageIndex === photosList.length - 1}
						aria-label="Imagen siguiente"
					>
						<svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"
							></path>
						</svg>
					</button>

					<div
						class="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-sm text-white"
					>
						{currentDialogImageIndex + 1} / {photosList.length}
					</div>
				{/if}

				<div
					class="absolute top-4 right-4 hidden rounded-md bg-black/50 px-2 py-1 text-xs text-white opacity-70 md:block"
				>
					← → para navegar
				</div>
			</div>
		{/if}
	</Dialog.Content>
</Dialog.Root>
